import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import adminAuthService from '../../utils/adminAuthService';
import { toast } from 'react-toastify';
import AdminNavbar from './AdminNavbar';
import AdminSidebar from './AdminSidebar';
import DashboardHeader from './dashboard/DashboardHeader';
import SalesTimeframeCards from './dashboard/SalesTimeframeCards';
import CustomersCard from './dashboard/CustomersCard';
import TopProducts from './dashboard/TopProducts';
import RecentTransactions from './dashboard/RecentTransactions';
import { ToastContainer } from 'react-toastify';
import RecentOrders from './dashboard/RecentOrders';


const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

const AdminHome = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Authentication validation
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = adminAuthService.getToken();
        if (!token) {
          throw new Error('No valid token available');
        }

        const response = await fetch(`${API_BASE_URL}/api/admin/validate-token/`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to validate token');
        }

        const user = await response.json();
        setUserData(user);
        setIsLoading(false);
      } catch (err) {
        console.error('Fetch user data error:', err.message);
        setError(err.message || 'Failed to load dashboard');
        setIsLoading(false);
        toast.error(err.message || 'Failed to load dashboard');
      }
    };

    fetchUserData();
  }, []);

  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    adminAuthService.logout();
    navigate('/admin/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-indigo-600 mx-auto mb-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4zm2 5a7.9 7.9 0 01-2-5H0c0 3 1.2 5.7 3 7.7l3-2.7z" />
          </svg>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-red-600 text-center mb-4">Authentication Error</h2>
          <p className="text-gray-700 text-center mb-6">{error}</p>
          <button
            onClick={() => navigate('/admin/login', { replace: true })}
            className="w-full py-2 px-4 rounded-md text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 p-4">
        <ToastContainer />
        <div className="text-center max-w-md">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">Desktop View Required</h3>
          <p className="mt-1 text-sm text-gray-500">
            The admin dashboard is optimized for desktop viewing. Please switch to a larger screen to access all features.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <ToastContainer />
      {/* Fixed Navbar with logout functionality */}
      <div className="fixed top-0 left-0 right-0 z-30">
        <AdminNavbar onLogout={handleLogout} userData={userData} />
      </div>
      
      <div className="flex flex-1 pt-16">
        {/* Fixed Sidebar */}
        <div className="fixed top-16 left-0 bottom-0 z-20">
          <AdminSidebar />
        </div>
        
        {/* Main Content */}
        <main className="flex-1 ml-72 pb-12">
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <DashboardHeader title="Dashboard Overview" />
            <SalesTimeframeCards />
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-4 space-y-8">
                <RecentTransactions />
                <RecentOrders/>
              </div>
              {/* <div className="space-y-8">
                <CustomersCard />
                <TopProducts />
              </div> */}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminHome;